/* Brand pages + Brands hub: collection filters, path links, sources dialog, gallery lightbox,
   featured-brand slider and the A-Z directory. Every customer-facing string comes from section settings
   (JSON config or data attributes); cards and entries are rendered by Liquid. */
(() => {
  'use strict';
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const fill = (s, map) => String(s || '').replace(/\{(\w+)\}/g, (m, k) => (k in map ? map[k] : m));
  const split = (v) => String(v || '').split('|').map((x) => x.trim()).filter(Boolean);
  const json = (el) => { try { return JSON.parse(el.textContent); } catch (e) { return null; } };

  /* ---------- Dialogs ---------- */
  let lastFocus = null;
  function openDialog(d, trigger) {
    if (!d) return;
    lastFocus = trigger || document.activeElement;
    if (typeof d.showModal === 'function') d.showModal(); else d.setAttribute('open', '');
    d.querySelector('[data-bp-close]')?.focus();
  }
  function closeDialog(d) {
    if (!d) return;
    if (typeof d.close === 'function') d.close(); else d.removeAttribute('open');
  }
  function wireDialog(d) {
    if (!d || d.__lsWired) return;
    d.__lsWired = true;
    d.addEventListener('click', (e) => {
      if (e.target.closest('[data-bp-close]') || e.target === d) closeDialog(d);
    });
    d.addEventListener('close', () => lastFocus?.focus({ preventScroll: true }));
  }

  /* ---------- Sources ("Behind the numbers") ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-bp-evidence]');
    if (!btn) return;
    const root = btn.closest('.ls-br') || document;
    const tpl = document.querySelector(`template[data-bp-evidence-template="${CSS.escape(btn.dataset.bpEvidence)}"]`);
    const dialog = root.querySelector('[data-bp-evidence-dialog]') || document.querySelector('[data-bp-evidence-dialog]');
    if (!tpl || !dialog) return;
    wireDialog(dialog);
    const body = dialog.querySelector('[data-bp-evidence-body]');
    body.replaceChildren(tpl.content.cloneNode(true));
    openDialog(dialog, btn);
  });

  /* ---------- Gallery lightbox ---------- */
  function initGallery(root) {
    const triggers = [...root.querySelectorAll('[data-bp-gallery-index]')];
    const d = root.querySelector('[data-bp-gallery-dialog]');
    if (!triggers.length || !d) return;
    wireDialog(d);
    const img = d.querySelector('[data-bp-lightbox-img]');
    const counter = d.querySelector('[data-bp-gallery-counter]');
    const caption = d.querySelector('[data-bp-gallery-caption]');
    const product = d.querySelector('[data-bp-gallery-product]');
    const field = (name) => d.querySelector(`[data-bp-gallery-${name}]`);
    const post = field('post');
    const stars = field('stars');
    const ratingLabel = d.dataset.ratingLabel || '';
    const setText = (el, value) => {
      if (!el) return;
      el.textContent = value || '';
      el.hidden = !value;
    };
    let i = 0;
    const show = (n) => {
      i = (n + triggers.length) % triggers.length;
      const t = triggers[i];
      const data = t.dataset;
      img.src = data.full;
      img.alt = t.querySelector('img')?.alt || data.caption || '';
      if (caption) caption.textContent = data.caption || '';
      setText(field('source'), data.source);
      setText(field('author'), data.author);
      setText(field('title'), data.title);
      setText(field('product-title'), data.productTitle);
      if (stars) {
        const r = Number(data.rating) || 0;
        stars.hidden = !r;
        stars.style.setProperty('--bp-ugc-fill', `${r * 20}%`);
        stars.setAttribute('aria-label', fill(ratingLabel, { n: r }));
      }
      if (post) {
        post.hidden = !data.postUrl;
        if (data.postUrl) post.href = data.postUrl;
      }
      if (counter) counter.textContent = fill(d.dataset.counter, { n: i + 1, total: triggers.length });
      if (product) {
        product.hidden = !data.productUrl;
        if (data.productUrl) product.href = data.productUrl;
      }
      [i - 1, i + 1].forEach((k) => {
        const next = triggers[(k + triggers.length) % triggers.length];
        if (next && next !== t) new Image().src = next.dataset.full;
      });
    };
    const more = root.querySelector('[data-bp-ugc-more-btn]');
    if (more) {
      more.addEventListener('click', () => {
        const step = Number(more.dataset.step) || 8;
        const rest = [...root.querySelectorAll('[data-bp-ugc-more][hidden]')];
        rest.slice(0, step).forEach((li) => { li.hidden = false; });
        const first = rest[0]?.querySelector('a');
        if (rest.length <= step) more.closest('.bp-ugc-actions').hidden = true;
        if (first) first.focus({ preventScroll: true });
      });
    }
    triggers.forEach((t, n) => t.addEventListener('click', (e) => { e.preventDefault(); show(n); openDialog(d, t); }));
    d.querySelector('[data-bp-gallery-prev]')?.addEventListener('click', () => show(i - 1));
    d.querySelector('[data-bp-gallery-next]')?.addEventListener('click', () => show(i + 1));
    d.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') show(i - 1);
      if (e.key === 'ArrowRight') show(i + 1);
    });
  }

  /* ---------- Sound selector (brand pages) ---------- */
  function initSound(root) {
    if (root.dataset.soundReady) return;
    root.dataset.soundReady = '1';
    const tabs = [...root.querySelectorAll('[role="tab"]')];
    const line = root.querySelector('[data-snd-line]');
    const area = root.querySelector('[data-snd-area]');
    if (!tabs.length) return;
    const W = 600, H = 220, MID = 110, DB = 9, N = 7;
    const gains = (tab) => {
      const g = String(tab.dataset.curve || '').split(',').map(Number).filter((n) => !Number.isNaN(n));
      const src = g.length >= 2 ? g : [0, 0, 0, 0, 0, 0, 0];
      return Array.from({ length: N }, (_, i) => {
        const x = (i / (N - 1)) * (src.length - 1), a = Math.floor(x), b = Math.min(a + 1, src.length - 1);
        return src[a] + (src[b] - src[a]) * (x - a);
      });
    };
    const path = (g) => {
      const pts = g.map((v, i) => [(i / (g.length - 1)) * W, Math.max(8, Math.min(H - 8, MID - v * DB))]);
      let d = `M${pts[0][0].toFixed(1)} ${pts[0][1].toFixed(1)}`;
      for (let i = 0; i < pts.length - 1; i++) {
        const p0 = pts[i - 1] || pts[i], p1 = pts[i], p2 = pts[i + 1], p3 = pts[i + 2] || p2;
        const c1 = [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6];
        const c2 = [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6];
        d += ` C${c1[0].toFixed(1)} ${c1[1].toFixed(1)} ${c2[0].toFixed(1)} ${c2[1].toFixed(1)} ${p2[0].toFixed(1)} ${p2[1].toFixed(1)}`;
      }
      return d;
    };
    const draw = (g) => {
      if (!line) return;
      const d = path(g);
      line.setAttribute('d', d);
      if (area) area.setAttribute('d', `${d} L${W} ${H} L0 ${H} Z`);
    };
    let now = gains(tabs.find((t) => t.getAttribute('aria-selected') === 'true') || tabs[0]);
    let raf = 0;
    draw(now);
    const morph = (to) => {
      cancelAnimationFrame(raf);
      if (reduced.matches || !line) { now = to; draw(now); return; }
      const from = now.slice(), t0 = performance.now(), dur = 560;
      const step = (t) => {
        const k = Math.min(1, (t - t0) / dur), e = 1 - Math.pow(1 - k, 4);
        now = from.map((v, i) => v + (to[i] - v) * e);
        draw(now);
        if (k < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    };
    const select = (tab, focus) => {
      tabs.forEach((t) => {
        const on = t === tab;
        t.setAttribute('aria-selected', String(on));
        t.tabIndex = on ? 0 : -1;
        const panel = document.getElementById(t.getAttribute('aria-controls'));
        if (panel) {
          panel.hidden = !on;
          panel.classList.toggle('is-entering', on && !reduced.matches);
        }
      });
      if (focus) tab.focus();
      morph(gains(tab));
    };
    tabs.forEach((tab) => tab.addEventListener('click', () => select(tab, false)));
    root.querySelector('[role="tablist"]')?.addEventListener('keydown', (e) => {
      const i = tabs.indexOf(document.activeElement);
      if (i < 0) return;
      const keys = { ArrowDown: i + 1, ArrowRight: i + 1, ArrowUp: i - 1, ArrowLeft: i - 1, Home: 0, End: tabs.length - 1 };
      if (!(e.key in keys)) return;
      e.preventDefault();
      select(tabs[(keys[e.key] + tabs.length) % tabs.length], true);
    });
    root.addEventListener('animationend', (e) => e.target.classList?.remove('is-entering'));
  }

  /* ---------- Brand collection ---------- */
  const FACETS = ['sound', 'tech', 'genre', 'use', 'config'];
  const KEYS = ['q', 'category', 'band', 'stock', ...FACETS, 'sort'];

  function initCatalog(root) {
    const cfg = json(root.querySelector('[data-ls-brand-config]')) || {};
    const t = cfg.t || {};
    const pageSize = Number(cfg.pageSize) || 6;
    const grid = root.querySelector('[data-role="products"]');
    const empty = root.querySelector('[data-role="empty"]');
    const more = root.querySelector('[data-action="more"]');
    const status = root.querySelector('[data-role="product-count"]');
    const active = root.querySelector('[data-role="active-filters"]');
    const clearBtns = [...root.querySelectorAll('[data-action="clear"]')];
    const heading = root.querySelector('[data-role="catalog-heading"]');
    const controls = Object.fromEntries([...root.querySelectorAll('[data-filter]')].map((el) => [el.dataset.filter, el]));
    const items = [...grid.querySelectorAll('.br-product, .ls-br-item')].map((el) => ({
      el,
      index: Number(el.dataset.index),
      cat: el.dataset.cat || '',
      price: Number(el.dataset.price) / 100,
      name: el.dataset.name || '',
      sound: split(el.dataset.sound),
      tech: split(el.dataset.tech),
      genre: split(el.dataset.genre),
      use: split(el.dataset.use),
      config: split(el.dataset.config),
      available: el.dataset.available !== '0',
      text: (el.dataset.name + ' ' + el.dataset.tech + ' ' + el.dataset.sound).toLowerCase(),
    }));
    /* "Recommended" = in stock first, then category order set in the editor (IEMs first by default), then collection order. */
    const catOrder = [...(controls.category?.options || [])].map((o) => o.value).filter(Boolean);
    const rank = (it) => { const i = catOrder.indexOf(it.cat); return i < 0 ? catOrder.length : i; };
    items.sort((a, b) => (b.available - a.available) || rank(a) - rank(b) || a.index - b.index).forEach((it, i) => { it.index = i; });

    /* Budget bands: USD breaks converted to the shopper's currency. */
    const rate = Number(window.Shopify?.currency?.rate) || 1;
    const currency = window.Shopify?.currency?.active || 'USD';
    const fmt = new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency, maximumFractionDigits: 0 });
    const breaks = String(cfg.breaks || '').split(',').map((x) => Number(x.trim())).filter((x) => x > 0).sort((a, b) => a - b);
    const bands = [];
    let lo = 0;
    for (const b of breaks) { bands.push({ id: `${lo}-${b}`, min: lo * rate, max: b * rate, label: fill(t.bandRange, { min: fmt.format(Math.round(lo * rate)), max: fmt.format(Math.round(b * rate)) }) }); lo = b; }
    if (breaks.length) bands.push({ id: `over-${lo}`, min: lo * rate, max: Infinity, label: fill(t.bandOver, { min: fmt.format(Math.round(lo * rate)) }) });
    const bandSelect = controls.band;
    if (bandSelect) for (const b of bands) bandSelect.add(new Option(b.label, b.id));

    /* Facet selects from the values products carry. */
    for (const key of FACETS) {
      const sel = controls[key];
      if (!sel) continue;
      const values = [...new Set(items.flatMap((it) => it[key]))].sort((a, b) => a.localeCompare(b));
      for (const v of values) sel.add(new Option(v, v));
      const field = root.querySelector(`[data-facet-field="${key}"]`);
      if (field) field.hidden = values.length === 0;
    }
    const refinements = root.querySelector('[data-role="refinements"]');
    if (refinements) refinements.hidden = FACETS.every((k) => root.querySelector(`[data-facet-field="${k}"]`)?.hidden !== false);
    const catSelect = controls.category;

    const state = Object.fromEntries(KEYS.map((k) => [k, '']));
    const params = new URLSearchParams(location.search);
    for (const k of KEYS) {
      const v = params.get(k);
      if (v && v.length < 150) state[k] = v;
    }
    let shown = pageSize;

    const matches = (it, skip) => {
      if (skip !== 'q' && state.q && !it.text.includes(state.q.toLowerCase())) return false;
      if (skip !== 'category' && state.category && it.cat !== state.category) return false;
      if (skip !== 'stock' && controls.stock && !it.available && (state.stock === 'in' || (!state.stock && controls.stock.dataset.stockDefault !== 'all'))) return false;
      if (skip !== 'band' && state.band) {
        const b = bands.find((x) => x.id === state.band);
        if (b && !(it.price >= b.min && it.price <= b.max)) return false;
      }
      for (const k of FACETS) if (skip !== k && state[k] && !it[k].includes(state[k])) return false;
      return true;
    };

    function syncOptions() {
      const count = (key, fn) => items.filter((it) => matches(it, key) && fn(it)).length;
      if (catSelect) for (const o of catSelect.options) {
        if (!o.value) continue;
        const n = count('category', (it) => it.cat === o.value);
        o.hidden = o.disabled = n === 0 && o.value !== state.category;
        o.textContent = `${o.dataset.label} (${n})`;
      }
      if (bandSelect) for (const o of bandSelect.options) {
        if (!o.value) continue;
        const b = bands.find((x) => x.id === o.value);
        const n = count('band', (it) => it.price >= b.min && it.price <= b.max);
        o.hidden = o.disabled = n === 0 && o.value !== state.band;
      }
      for (const key of FACETS) {
        const sel = controls[key];
        if (!sel) continue;
        for (const o of sel.options) {
          if (!o.value) continue;
          const n = count(key, (it) => it[key].includes(o.value));
          o.hidden = o.disabled = n === 0 && o.value !== state[key];
          o.textContent = `${o.value} (${n})`;
        }
      }
    }

    const CLOSE = (window.LsIcons && window.LsIcons.svg('close')) || '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6 6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
    const advCount = root.querySelector('[data-role="advanced-count"]');
    function chips() {
      active.querySelectorAll('[data-clear]').forEach((c) => c.remove());
      for (const k of KEYS) {
        if (k === 'sort' || !state[k]) continue;
        const sel = controls[k];
        const opt = sel && sel.tagName === 'SELECT' ? sel.selectedOptions[0] : null;
        const label = opt ? (k === 'band' ? opt.textContent : opt.dataset.label || opt.value || state[k]) : state[k];
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'lt-chip';
        b.dataset.clear = k;
        b.setAttribute('aria-label', fill(t.remove, { value: label }));
        b.innerHTML = '<span></span>' + CLOSE;
        b.firstChild.textContent = label;
        active.append(b);
      }
      for (const [k, c] of Object.entries(controls)) c.closest('.lt-field')?.classList.toggle('is-set', !!state[k]);
      if (advCount) {
        const n = FACETS.filter((k) => state[k] && controls[k]).length;
        advCount.textContent = n;
        advCount.hidden = !n;
      }
    }

    function writeURL() {
      const u = new URL(location.href);
      for (const k of KEYS) { if (state[k]) u.searchParams.set(k, state[k]); else u.searchParams.delete(k); }
      history.replaceState(history.state, '', u);
    }

    function render() {
      for (const k of KEYS) if (controls[k] && controls[k].value !== state[k]) controls[k].value = state[k];
      const list = items.filter((it) => matches(it));
      const sort = state.sort;
      list.sort((a, b) => (sort === 'price-low' ? a.price - b.price : sort === 'price-high' ? b.price - a.price : sort === 'name' ? a.name.localeCompare(b.name) : 0) || a.index - b.index);
      const visible = new Set(list.slice(0, shown));
      const order = sort ? list.concat(items.filter((it) => !list.includes(it))) : items;
      for (const it of order) { grid.insertBefore(it.el, empty); it.el.hidden = !visible.has(it); }
      const n = list.length;
      status.textContent = n === 0 ? t.countNone : n === 1 ? t.countOne : fill(t.countOther, { count: n });
      empty.hidden = n > 0;
      const left = n - visible.size;
      more.hidden = left <= 0;
      more.textContent = fill(t.more, { count: Math.min(pageSize, left) });
      const any = KEYS.some((k) => state[k]);
      clearBtns.forEach((b) => { if (!b.closest('[data-role="empty"]')) b.hidden = !any; });
      syncOptions();
      chips();
      writeURL();
    }

    const set = (k, v) => { state[k] = v; shown = pageSize; render(); };
    for (const [k, el] of Object.entries(controls)) {
      el.addEventListener(el.tagName === 'INPUT' ? 'input' : 'change', () => set(k, el.value.trim()));
    }
    more.addEventListener('click', () => {
      const first = items.find((it) => it.el.hidden && matches(it));
      shown += pageSize;
      render();
      first?.el.querySelector('a')?.focus({ preventScroll: true });
    });
    root.addEventListener('click', (e) => {
      const chip = e.target.closest('[data-clear]');
      if (chip) {
        const list = [...active.querySelectorAll('[data-clear]')];
        const i = list.indexOf(chip);
        set(chip.dataset.clear, '');
        const next = [...active.querySelectorAll('[data-clear]')];
        (next[i] || next[i - 1] || controls[chip.dataset.clear])?.focus({ preventScroll: true });
        return;
      }
      if (e.target.closest('[data-action="clear"]')) {
        for (const k of KEYS) state[k] = '';
        shown = pageSize;
        render();
      }
    });

    /* Category chips and sound paths elsewhere on the page filter this collection in place. */
    document.addEventListener('click', (e) => {
      const a = e.target.closest('[data-path-key]');
      if (!a || !(a.dataset.pathKey in state)) return;
      e.preventDefault();
      for (const k of KEYS) if (k !== 'sort') state[k] = '';
      state[a.dataset.pathKey] = a.dataset.pathValue;
      shown = pageSize;
      render();
      const target = heading || root;
      target.scrollIntoView({ behavior: reduced.matches ? 'auto' : 'smooth', block: 'start' });
      heading?.focus({ preventScroll: true });
    });

    render();
  }

  /* ---------- Hub: featured-brand slider ---------- */
  function initShowcase(root) {
    const panels = [...root.querySelectorAll('[data-bp-slide]')];
    if (panels.length < 2) return;
    const tabs = [...root.querySelectorAll('[data-bp-tab]')];
    const followups = [...root.querySelectorAll('[data-bp-followup]')];
    const select = root.querySelector('[data-bp-select]');
    const counter = root.querySelector('[data-bp-counter]');
    const status = root.querySelector('[data-bp-status]');
    const play = root.querySelector('[data-bp-play]');
    const ds = root.dataset;
    const delay = (Number(ds.interval) || 7) * 1000;
    let index = 0, autoplay = false, hovered = false, timer = 0;
    const pad = (n) => String(n).padStart(2, '0');

    const updatePlay = () => {
      if (!play) return;
      play.setAttribute('aria-pressed', String(autoplay));
      play.setAttribute('aria-label', autoplay ? ds.pauseLabel : ds.playLabel);
      play.querySelectorAll('[data-icon]').forEach((s) => { s.hidden = s.dataset.icon !== (autoplay ? 'pause' : 'play'); });
      play.disabled = reduced.matches;
    };
    const schedule = () => {
      clearTimeout(timer);
      if (autoplay && !hovered && !reduced.matches && !document.hidden) timer = setTimeout(() => go(index + 1, false), delay);
    };
    function go(next, user = true) {
      const prior = index;
      index = (next + panels.length) % panels.length;
      if (user && autoplay) { autoplay = false; updatePlay(); }
      panels.forEach((p, i) => { p.hidden = i !== index; p.inert = i !== index; });
      tabs.forEach((tab, i) => { tab.setAttribute('aria-selected', String(i === index)); tab.tabIndex = i === index ? 0 : -1; });
      const id = panels[index].dataset.bpSlide;
      followups.forEach((f) => { f.hidden = f.dataset.bpFollowup !== id; });
      panels[index].querySelectorAll('img[loading="lazy"]').forEach((im) => { im.loading = 'eager'; });
      if (select) select.value = String(index);
      if (counter) counter.textContent = `${pad(index + 1)} / ${pad(panels.length)}`;
      if (status && user) status.textContent = fill(ds.status, { brand: tabs[index]?.textContent.trim() || '' });
      const copy = panels[index].querySelector('.bp-hero-copy');
      if (window.gsap && copy && prior !== index && !reduced.matches) {
        window.gsap.fromTo(copy, { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.35, ease: 'power2.out', clearProps: 'opacity,transform', overwrite: true });
      }
      schedule();
    }
    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => go(i));
      tab.addEventListener('keydown', (e) => {
        const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
        if (e.key === 'Home' || e.key === 'End') { e.preventDefault(); go(e.key === 'Home' ? 0 : panels.length - 1); tabs[index].focus(); }
        else if (step) { e.preventDefault(); go(index + step); tabs[index].focus(); }
      });
    });
    select?.addEventListener('change', () => go(Number(select.value)));
    root.querySelector('[data-bp-prev]')?.addEventListener('click', () => go(index - 1));
    root.querySelector('[data-bp-next]')?.addEventListener('click', () => go(index + 1));
    play?.addEventListener('click', () => { autoplay = !autoplay; updatePlay(); schedule(); });
    root.addEventListener('pointerenter', () => { hovered = true; clearTimeout(timer); });
    root.addEventListener('pointerleave', () => { hovered = false; schedule(); });
    root.addEventListener('focusin', () => { hovered = true; clearTimeout(timer); });
    root.addEventListener('focusout', (e) => { if (!root.contains(e.relatedTarget)) { hovered = false; schedule(); } });
    document.addEventListener('visibilitychange', schedule);
    reduced.addEventListener?.('change', () => { if (reduced.matches) autoplay = false; updatePlay(); schedule(); });
    updatePlay();
    go(0, false);
  }

  /* ---------- Hub: A-Z directory ---------- */
  function initDirectory(root) {
    const entries = [...root.querySelectorAll('.br-directory-entry')].map((el) => ({
      el, name: (el.dataset.name || '').toLowerCase(), cats: split(el.dataset.cats), letter: el.dataset.letter || '',
      text: (el.dataset.name + ' ' + (el.dataset.catNames || '')).toLowerCase(),
    }));
    const search = root.querySelector('[data-role="directory-search"]');
    const cat = root.querySelector('[data-role="directory-category"]');
    const letters = [...root.querySelectorAll('[data-letter]')].filter((b) => b.tagName === 'BUTTON');
    const count = root.querySelector('[data-role="directory-count"]');
    const reset = root.querySelector('[data-action="reset-directory"]');
    const ds = root.dataset;
    const list = root.querySelector('[data-role="directory-list"]');
    entries.sort((a, b) => a.name.localeCompare(b.name)).forEach((e) => list.append(e.el));
    const state = { q: '', cat: '', letter: '' };
    if (cat) for (const o of cat.options) if (o.value && !entries.some((e) => e.cats.includes(o.value))) o.hidden = o.disabled = true;
    function render() {
      let n = 0;
      const present = new Set();
      for (const e of entries) {
        const base = (!state.q || e.text.includes(state.q)) && (!state.cat || e.cats.includes(state.cat));
        if (base) present.add(e.letter);
        const ok = base && (!state.letter || e.letter === state.letter);
        e.el.hidden = !ok;
        if (ok) n++;
      }
      letters.forEach((b) => {
        b.disabled = Boolean(b.dataset.letter) && !present.has(b.dataset.letter) && b.dataset.letter !== state.letter;
        b.setAttribute('aria-pressed', String(b.dataset.letter === state.letter));
      });
      const total = entries.length;
      count.textContent = n === 0 ? ds.countNone : n === total ? fill(ds.countAll, { count: total }) : fill(ds.countSome, { count: n, total });
      if (reset) reset.hidden = !(state.q || state.cat || state.letter);
    }
    search?.addEventListener('input', () => { state.q = search.value.trim().toLowerCase(); render(); });
    cat?.addEventListener('change', () => { state.cat = cat.value; render(); });
    letters.forEach((b) => b.addEventListener('click', () => { state.letter = b.dataset.letter; render(); }));
    reset?.addEventListener('click', () => {
      state.q = state.cat = state.letter = '';
      if (search) search.value = '';
      if (cat) cat.value = '';
      render();
    });
    render();
  }

  /* Brand pages using Minimog's collection grid: give it the anchor the brand sections link to. */
  function anchorCollection() {
    if (document.getElementById('collection')) return;
    const grid = document.getElementById('CollectionProductGrid');
    const target = grid && (grid.closest('.shopify-section') || grid);
    if (!target || !document.querySelector('.ls-br')) return;
    target.id = 'collection';
    target.classList.add('ls-br-collection');
    if (location.hash === '#collection') requestAnimationFrame(() => target.scrollIntoView());
  }

  function init() {
    anchorCollection();
    document.querySelectorAll('[data-ls-brand-catalog]').forEach(initCatalog);
    document.querySelectorAll('[data-ls-brand-gallery]').forEach(initGallery);
    document.querySelectorAll('[data-ls-brand-sound]').forEach(initSound);
    document.querySelectorAll('[data-ls-brands-showcase]').forEach(initShowcase);
    document.querySelectorAll('[data-ls-brands-directory]').forEach(initDirectory);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
})();
